import React, { useCallback, useEffect } from 'react';
import { Dialog } from '@mui/material';
import {
  type IRemoteNotification,
  SuprSendFeedProvider,
  useFeedClient
} from '@suprsend/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { useSnackbar } from '@/hooks/useSnackbar';

import {
  NOTIFICATION_FEED_OPTIONS,
  useNotificationFeed
} from '../hooks/useNotificationFeed';

import { mobileNotificationsOpenAtom } from './mobileNotificationsOpenAtom';
import { NotificationBellButton } from './NotificationBellButton';
import NotificationsPanel from './NotificationsPanel';
import { notificationFromSuprSend } from './notificationFromSuprSend';
import { notificationUnreadCountAtom } from './notificationUnreadCountAtom';

const MobileFeedSync: React.FC<{
  onNewNotification: (data: IRemoteNotification) => void;
}> = ({ onNewNotification }) => {
  const feedClient = useFeedClient();
  const { unreadCount } = useNotificationFeed();
  const setUnreadCount = useSetAtom(notificationUnreadCountAtom);

  useEffect(() => {
    setUnreadCount(unreadCount);
  }, [unreadCount, setUnreadCount]);

  useEffect(() => {
    if (!feedClient?.emitter) {
      return;
    }
    feedClient.emitter.on('feed.new_notification', onNewNotification);
    return () => {
      feedClient.emitter?.off('feed.new_notification', onNewNotification);
    };
  }, [feedClient, onNewNotification]);

  return null;
};

const MobileNotificationsDialog: React.FC = () => {
  const [open, setOpen] = useAtom(mobileNotificationsOpenAtom);

  return (
    <Dialog fullScreen open={open} onClose={() => setOpen(false)}>
      <NotificationsPanel layout="fullScreen" onClose={() => setOpen(false)} />
    </Dialog>
  );
};

export const MobileNotificationsBell: React.FC = () => {
  const unreadCount = useAtomValue(notificationUnreadCountAtom);
  const setOpen = useSetAtom(mobileNotificationsOpenAtom);

  return (
    <NotificationBellButton
      badgeCount={unreadCount}
      onClick={() => setOpen(true)}
    />
  );
};

export const MobileInboxWithNotifications: React.FC = () => {
  const { showSnackbar, closeSnackbar } = useSnackbar();

  const handleNewNotification = useCallback(
    (data: IRemoteNotification) => {
      const notification = notificationFromSuprSend(data);
      const key = showSnackbar({
        message: notification.message.title,
        severity: 'info'
      });
      setTimeout(() => closeSnackbar(key), 6000);
    },
    [showSnackbar, closeSnackbar]
  );

  return (
    <SuprSendFeedProvider {...NOTIFICATION_FEED_OPTIONS}>
      <MobileFeedSync onNewNotification={handleNewNotification} />
      <MobileNotificationsDialog />
    </SuprSendFeedProvider>
  );
};
